import { BaseZenputCollection, BaseZenputModel, Paginator } from '../forms';
import shared_sensors_utils from './util';

export const SensorSparkline = BaseZenputModel.extend({
    legacy_classname: '$f.shared.sensors.models.SensorSparkline',

    defaults: {
        timestamps: [],
        values: [],
        id: null,
        lookback_period: shared_sensors_utils.LOOKBACK_PERIODS.HALF_DAY,
        name: null
    },

    set_lookback_period: function (value) {
        switch (value) {
            case shared_sensors_utils.LOOKBACK_PERIODS.HALF_DAY:
            case shared_sensors_utils.LOOKBACK_PERIODS.DAY:
            case shared_sensors_utils.LOOKBACK_PERIODS.WEEK:
            case shared_sensors_utils.LOOKBACK_PERIODS.MONTH:
            case shared_sensors_utils.LOOKBACK_PERIODS.THREE_MONTHS:
            case shared_sensors_utils.LOOKBACK_PERIODS.SIX_MONTHS:
            case shared_sensors_utils.LOOKBACK_PERIODS.YEAR:
                this.set('lookback_period', value);
                break;
            default:
                throw new Error('Programmer error: Unknown lookback period');
        }
    },

    url: function () {
        return `/api/v2/sensors/sparkline/${this.get('id')}/?lookback_period=${this.get('lookback_period')}`;
    }
});

export const SensorRelatedAccount = BaseZenputModel.extend({
    legacy_classname: '$f.shared.sensors.models.SensorRelatedAccount',

    defaults: {
        id: null,
        name: null,
        time_zone: null
    }
});

export const SensorIntegration = BaseZenputModel.extend({
    legacy_classname: '$f.shared.sensors.models.SensorIntegration',

    defaults: {
        id: null,
        integration_type: null,
        integration_type_name: null
    }
});

export const SensorPlacement = BaseZenputModel.extend({
    legacy_classname: '$f.shared.sensors.models.Placement',

    defaults: {
        id: null,
        name: null,
        sensor_placement_nicknames: null
    },

    url: function () {
        return `/api/v2/sensors/placements/${this.get('id')}/`;
    }
});

export const SensorPlacementsPaginator = Paginator.extend({
    legacy_classname: '$f.shared.sensors.models.SensorPlacementsPaginator',

    defaults: $.extend({}, Paginator.prototype.defaults, {
        order_by: '-name',
        limit: 50
    })
});

export const SensorsPlacements = BaseZenputCollection.extend({
    legacy_classname: '$f.shared.sensors.collections.SensorsPlacements',

    model: SensorPlacement,
    typeahead_search_attrs: ['name'],
    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);

        if (!this.paginator) {
            this.paginator = new SensorPlacementsPaginator();
        }
    },

    url: function () {
        const options = this.models.find((model) => model.get('fetchIds'));
        if (options && options.get('fetchIds').length) {
            return `/api/v2/sensors/placements/?id=${encodeURIComponent(options.get('fetchIds'))}`;
        }
        return `/api/v2/sensors/placements/?${this.paginator.get_query_string()}`;
    }
});

export const SensorStatus = BaseZenputModel.extend({
    legacy_classname: '$f.shared.sensors.models.Status',

    defaults: {
        id: null,
        name: null
    }
});

export const SensorsStatus = BaseZenputCollection.extend({
    legacy_classname: '$f.shared.sensors.collections.SensorsStatus',

    paginator: null,
    model: SensorStatus,
    typeahead_search_attrs: ['name'],
    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);
    },
    sync: function () {
        return null;
    },
    fetch: function () {
        return null;
    },
    save: function () {
        return null;
    }
});

export const Sensor = BaseZenputModel.extend({
    legacy_classname: '$f.shared.sensors.models.Sensor',

    defaults: {
        id: null,
        name: null,
        date_created: null,
        date_modified: null,
        account: null,
        integration: null,
        last_reading_value: null,
        placement: null,
        sensor_id: null,
        check_digit: null,
        is_online: false,
        battery: null,
        in_use: false,
        is_enabled: true,
        manufacturer: null,
        equipment_location: null
    },

    submodels: {
        account: SensorRelatedAccount,
        integration: SensorIntegration,
        placement: SensorPlacement
    },

    url: '/api/v2/sensors/sensors/',

    validate: function (attrs) {
        if (!attrs.name?.trim()) {
            const field = _t('mobile_sensors_nickname');
            return { field, message: _t('mobile_sensors_field_not_blank', { field }) };
        }
    },

    sync: function (method, model, options) {
        if (options && options.use_id_param) {
            options.url = `${model.url}${model.id}/`;
        } else {
            options.url = model.url;
        }

        return Backbone.sync(method, model, options);
    },

    get_last_reading_value: function () {
        return $f.utils.convert_to_users_temperature_units(this.get('last_reading_value'));
    },

    get_last_reading_date: function () {
        return shared_sensors_utils.get_formatted_sensor_timestamp({
            timestamp: this.get('last_reading_date'),
            format: $f.utils.TIME_FMT,
            account: this.get('account')
        });
    },

    get_last_reading_timedelta_str: function () {
        return shared_sensors_utils.get_sensor_timedelta_str_repr({
            timestamp: this.get('last_reading_date')
        });
    },

    get_placement_for_display: function () {
        var placement = this.get('placement');
        return placement ? placement.get('name') : '';
    },

    is_initializing: function () {
        return !this.get_last_reading_value();
    },

    update_barcode_value: function (barcode, manufacturer) {
        if (manufacturer == $f.mobile.sensors.util.MANUFACTURER.MONNIT) {
            this.set($f.mobile.sensors.util.get_sensor_ids_from_scanner(barcode));
        } else if (manufacturer == $f.mobile.sensors.util.MANUFACTURER.MACHINEQ) {
            this.set($f.mobile.sensors.util.get_info_from_machineq_sensor_barcode(barcode));
        } else {
            const is_sensor = true;
            this.set($f.mobile.sensors.util.get_info_from_lorawan_barcode(barcode, is_sensor));
        }
    },

    set_device_id: function (value) {
        this.set('sensor_id', value);
    },

    get_device_id: function () {
        return this.get('sensor_id');
    },

    sanitize_device_id: function () {
        const device_id = this.get_device_id();
        this.set_device_id(shared_sensors_utils.sanitize_dev_eui(device_id.toUpperCase()));
    }
});

export const SensorsPaginator = Paginator.extend({
    legacy_classname: '$f.shared.sensors.models.SensorsPaginator',

    defaults: $.extend({}, Paginator.prototype.defaults, {
        order_by: '-is_enabled,-is_online,-name',
        limit: 25 // Default DRF endpoint of 20 + 5.
    }),

    override_default_query_string_attrs: ['start', 'limit', 'order_by', 'account_id', 'name', 'group_id'],

    initialize: function () {
        // override the additions of unnecessary query strings from the base class.
        SensorsPaginator.__super__.initialize.apply(this, arguments);
        this.default_query_string_attrs = this.override_default_query_string_attrs;
    }
});

export const Sensors = BaseZenputCollection.extend({
    legacy_classname: '$f.shared.sensors.collections.Sensors',

    model: Sensor,

    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);

        if (!this.paginator) {
            this.paginator = new SensorsPaginator();
        }
    },

    url: function () {
        return `/api/v2/sensors/sensors/?${this.paginator.get_query_string()}`;
    }
});
